import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type { ViewerRef } from '@editorComponents'

/** A plan PDF in the preview, by plan guid and the url it was loaded from. */
export interface PlanPreviewDocument {
  guid: string
  url: string
}

interface PlanPdfViewerContextValue {
  /** Handle of the "Pläne" tab's PDF preview viewer; null while it is not mounted. */
  viewer: ViewerRef | null
  /** Callback ref for the preview's `<Viewer>`. */
  setViewer: (viewer: ViewerRef | null) => void
  /**
   * Plan PDF changed in the preview and not saved or discarded yet. Carries the url the changes
   * were made on: the plan list signs its urls anew on every fetch, and the preview keeps showing
   * this url (instead of reloading the fresh one, which would drop the changes) until it is
   * saved or discarded.
   */
  changedDocument: PlanPreviewDocument | null
  setChangedDocument: (changedDocument: PlanPreviewDocument | null) => void
  /**
   * Plan PDF the preview has loaded. The plan list signs its urls anew on every fetch (e.g. after a
   * Kommentar/Tags autosave or a window refocus); the preview keeps showing this url for the plan,
   * so a refetch doesn't reload the unchanged PDF (losing zoom and scroll position). Cleared when
   * the plan's PDF itself changes (saved), so the preview loads the new file.
   */
  loadedDocument: PlanPreviewDocument | null
  setLoadedDocument: (loadedDocument: PlanPreviewDocument | null) => void
}

const isSameDocument = (a: PlanPreviewDocument | null, b: PlanPreviewDocument | null) =>
  a?.guid === b?.guid && a?.url === b?.url

const PlanPdfViewerContext = createContext<PlanPdfViewerContextValue>({
  viewer: null,
  setViewer: () => undefined,
  changedDocument: null,
  setChangedDocument: () => undefined,
  loadedDocument: null,
  setLoadedDocument: () => undefined,
})

/**
 * Shares the plan preview's viewer (rendered in the scene pane) and its unsaved-change state with
 * the plan rows in the sidebar, which save/discard the edited PDF and run the PDF/A conversion on
 * the viewer's PDFNet engine.
 */
export const PlanPdfViewerProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [viewer, setViewerState] = useState<ViewerRef | null>(null)
  const [changedDocument, setChangedDocumentState] = useState<PlanPreviewDocument | null>(null)
  const [loadedDocument, setLoadedDocumentState] = useState<PlanPreviewDocument | null>(null)

  // Every annotation edit reports the (same) changed document; only an actual change may
  // re-render the context's consumers (the viewer, every plan row), or editing lags.
  const setChangedDocument = useCallback((next: PlanPreviewDocument | null) => {
    setChangedDocumentState(previous => (isSameDocument(previous, next) ? previous : next))
  }, [])

  const setLoadedDocument = useCallback((next: PlanPreviewDocument | null) => {
    setLoadedDocumentState(previous => (isSameDocument(previous, next) ? previous : next))
  }, [])

  const setViewer = useCallback(
    (nextViewer: ViewerRef | null) => {
      setViewerState(nextViewer)
      // The unsaved changes only exist inside the viewer: once it is gone (the preview unmounted,
      // e.g. by a route change outside the block-action gates), there is nothing left to save.
      if (!nextViewer) {
        setChangedDocument(null)
        setLoadedDocument(null)
      }
    },
    [setChangedDocument, setLoadedDocument],
  )

  const value = useMemo(
    () => ({
      viewer,
      setViewer,
      changedDocument,
      setChangedDocument,
      loadedDocument,
      setLoadedDocument,
    }),
    [viewer, setViewer, changedDocument, setChangedDocument, loadedDocument, setLoadedDocument],
  )
  return <PlanPdfViewerContext.Provider value={value}>{children}</PlanPdfViewerContext.Provider>
}

export const usePlanPdfViewer = (): PlanPdfViewerContextValue => useContext(PlanPdfViewerContext)
